import makeStyles from '@mui/styles/makeStyles';
import {SlidersHorizontal} from "lucide-react";
import {map} from 'lodash';
import React, {useState} from "react";
import InfiniteScroll from 'react-infinite-scroll-component';
import {useTranslation} from "react-i18next";
import {color, font, radius, type} from "common/tokens";
import useFreshData, {dataKeys} from "common/useFreshData";
import FeedFilterPopover from "components/FeedFilterPopover";
import DefaultPost from "components/PostTypes/DefaultPost";
import QuotePost from "components/PostTypes/QuotePost";
import Card from "components/redesign/Card";
import Skeleton from "components/redesign/Skeleton";

const useStyles = makeStyles({
  // The feed keeps its own height and its own scroll: the sessions beside it
  // are a short list, and the feed would otherwise run the page on forever.
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 210px)',
    minHeight: 420,
    position: 'sticky',
    top: 24,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 18px',
    borderBottom: `1px solid ${color.DIVIDER}`,
    flexShrink: 0,
  },
  eyebrow: {
    ...type.eyebrow,
    fontWeight: font.BOLD,
    letterSpacing: '1.43px',
    color: color.SECTION_TITLE,
    margin: 0,
    flex: 1,
  },
  filterButton: {
    appearance: 'none',
    width: 32,
    height: 32,
    borderRadius: radius.PILL,
    border: 'none',
    background: color.SURFACE_MUTED,
    color: color.TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowAnchor: 'none',
    padding: 14,
  },
  posts: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  end: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: '24px 0 8px',
  },
  loading: {
    paddingTop: 12,
  },
});

const FeedPostItem = props => {
  switch (props.type) {
    case 'post':
      return <DefaultPost {...props}/>;

    case 'quote':
      return <QuotePost {...props}/>;

    default:
      return null;
  }
};

const Feeds = ({
  feedPosts = [],
  getNextFeeds,
  initFeeds,
  endReached,
  postFeedLinkClick,
  toggleLike,
  setFeedFilter,
  applyFeedFilter,
  filter,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  useFreshData(dataKeys.FEEDS, initFeeds);

  return (
    <Card className={classes.panel}>
      <div className={classes.header}>
        <p className={classes.eyebrow}>{t('feeds')}</p>
        <button
          type={'button'}
          aria-label={t('filters')}
          className={classes.filterButton}
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <SlidersHorizontal size={15}/>
        </button>
      </div>

      <FeedFilterPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        setFeedFilter={setFeedFilter}
        applyFeedFilter={applyFeedFilter}
        filter={filter}
      />

      <div id={'feed-scroll'} className={classes.scroller}>
        <InfiniteScroll
          dataLength={feedPosts.length}
          next={getNextFeeds}
          hasMore={!endReached}
          scrollableTarget={'feed-scroll'}
          className={classes.posts}
          loader={
            <div className={classes.loading}>
              <Skeleton height={132}/>
            </div>
          }
          endMessage={<p className={classes.end}>{t('feedSeenAll')}</p>}
        >
          {map(feedPosts, item => (
            <FeedPostItem
              key={item.id}
              type={item.event}
              post={item}
              toggleLike={toggleLike}
              onClickCount={postFeedLinkClick}
            />
          ))}
        </InfiniteScroll>
      </div>
    </Card>
  );
};

export default Feeds;
